export interface InventorySlot {
  itemId: string;
  name: string;
  quantity: number;
  maxStack: number;
  unitMassKg: number;
}

export interface InventoryItemInput {
  itemId: string;
  name: string;
  quantity: number;
  maxStack?: number;
  unitMassKg?: number;
}

export interface InventoryOptions {
  slotCount?: number;
  maxMassKg?: number;
}

export class Inventory {
  private slots: (InventorySlot | null)[];
  public readonly slotCount: number;
  public readonly maxMassKg: number;

  constructor(options: InventoryOptions = {}) {
    this.slotCount = options.slotCount ?? 16;
    this.maxMassKg = options.maxMassKg ?? 100.0;
    this.slots = new Array(this.slotCount).fill(null);
  }

  public getSlots(): readonly (InventorySlot | null)[] {
    return this.slots;
  }

  public getTotalMassKg(): number {
    let mass = 0;
    for (const slot of this.slots) {
      if (slot) {
        mass += slot.quantity * slot.unitMassKg;
      }
    }
    return Math.round(mass * 1000) / 1000;
  }

  public getItemCount(itemId: string): number {
    let count = 0;
    for (const slot of this.slots) {
      if (slot && slot.itemId === itemId) {
        count += slot.quantity;
      }
    }
    return count;
  }

  public hasItem(itemId: string, minQuantity: number = 1): boolean {
    return this.getItemCount(itemId) >= minQuantity;
  }

  public isFull(): boolean {
    return this.slots.every((s) => s !== null);
  }

  public addItem(item: InventoryItemInput): number {
    const maxStack = item.maxStack ?? 64;
    const unitMass = item.unitMassKg ?? 0.1;

    // Check maximum weight limit
    const currentMass = this.getTotalMassKg();
    const remainingMass = Math.max(0, this.maxMassKg - currentMass);
    const maxAllowedByWeight = unitMass > 0 ? Math.floor(remainingMass / unitMass) : item.quantity;
    const fitQuantity = Math.min(item.quantity, maxAllowedByWeight);
    const rejectedByWeight = item.quantity - fitQuantity;

    let toAdd = fitQuantity;

    // Pass 1: Try stacking into existing non-full slots
    for (let i = 0; i < this.slotCount && toAdd > 0; i++) {
      const slot = this.slots[i];
      if (slot && slot.itemId === item.itemId && slot.quantity < slot.maxStack) {
        const canFit = slot.maxStack - slot.quantity;
        const addAmount = Math.min(canFit, toAdd);
        slot.quantity += addAmount;
        toAdd -= addAmount;
      }
    }

    // Pass 2: Place remaining into empty slots
    for (let i = 0; i < this.slotCount && toAdd > 0; i++) {
      if (this.slots[i] === null) {
        const addAmount = Math.min(maxStack, toAdd);
        this.slots[i] = {
          itemId: item.itemId,
          name: item.name,
          quantity: addAmount,
          maxStack,
          unitMassKg: unitMass
        };
        toAdd -= addAmount;
      }
    }

    return toAdd + rejectedByWeight;
  }

  public removeItem(itemId: string, quantity: number): boolean {
    if (!this.hasItem(itemId, quantity)) {
      return false;
    }

    let toRemove = quantity;
    for (let i = 0; i < this.slotCount && toRemove > 0; i++) {
      const slot = this.slots[i];
      if (slot && slot.itemId === itemId) {
        if (slot.quantity <= toRemove) {
          toRemove -= slot.quantity;
          this.slots[i] = null;
        } else {
          slot.quantity -= toRemove;
          toRemove = 0;
        }
      }
    }

    return true;
  }

  public clear(): void {
    this.slots.fill(null);
  }
}
